import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import SeverityBadge from "../components/SeverityBadge";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { formatIDR, formatPercent, formatDate } from "../lib/format";
import { ArrowLeft, GitBranch } from "lucide-react";

export default function FacilityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/facilities/${id}`).then((r) => setData(r.data));
  }, [id]);

  if (!data) return <div className="p-8 text-sm text-muted-foreground">Memuat…</div>;
  const f = data.facility;
  const ratio = f.utilization_ratio || 0;

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="facility-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/facilities")} className="mb-4">
        <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Kembali
      </Button>

      <PageHeader
        title={`Facility ${f.facility_id}`}
        subtitle={`${f.facility_type} · ${f.product_type}`}
        action={
          <div className="flex gap-2">
            {ratio > 1 && f.allow_excess ? <SeverityBadge severity="warning">EXCESS ACK</SeverityBadge> :
              ratio > 1 ? <SeverityBadge severity="critical">LIMIT BREACH</SeverityBadge> :
              ratio >= 0.9 ? <SeverityBadge severity="warning">HIGH UTILIZATION</SeverityBadge> : null}
            <Button variant="outline" onClick={() => navigate(`/graph?borrower=${f.borrower_id}`)}>
              <GitBranch className="h-4 w-4 mr-1.5" /> Lihat Graph
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="kpi-card">
          <div className="kpi-label">Approved Limit</div>
          <div className="kpi-value text-2xl">{formatIDR(f.approved_limit, { compact: true })}</div>
          <div className="kpi-sub">{formatIDR(f.approved_limit)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Utilized</div>
          <div className={`kpi-value text-2xl ${ratio > 1 ? "text-[#D0021B]" : ratio >= 0.9 ? "text-[#F5A623]" : ""}`}>
            {formatIDR(f.utilized_limit, { compact: true })}
          </div>
          <div className="kpi-sub mono">{formatPercent(ratio)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Blocked</div>
          <div className="kpi-value text-2xl">{formatIDR(f.blocked_amount, { compact: true })}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Available</div>
          <div className="kpi-value text-2xl">{formatIDR(f.available_limit, { compact: true })}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="border border-border rounded-md bg-card p-4">
          <div className="kpi-label">Tanggal</div>
          <div className="text-sm mt-2"><span className="text-muted-foreground">Mulai: </span><span className="mono">{formatDate(f.start_date)}</span></div>
          <div className="text-sm mt-1"><span className="text-muted-foreground">Jatuh tempo: </span><span className="mono">{formatDate(f.maturity_date)}</span></div>
        </div>
        <div className="border border-border rounded-md bg-card p-4">
          <div className="kpi-label">Suku Bunga</div>
          <div className="text-2xl font-bold mono mt-2">{f.interest_rate}%</div>
        </div>
        <div className="border border-border rounded-md bg-card p-4">
          <div className="kpi-label">Borrower</div>
          {data.borrower && (
            <button className="text-sm font-medium mt-2 hover:underline text-left" onClick={() => navigate(`/borrowers/${data.borrower.borrower_id}`)}>
              {data.borrower.name}
            </button>
          )}
        </div>
      </div>

      <div className="border border-border rounded-md bg-card p-4 mb-6">
        <div className="kpi-label mb-2">Tujuan Fasilitas</div>
        <div className="text-sm">{f.purpose || "-"}</div>
      </div>

      {f.allow_excess && (
        <div className="border-l-4 border-[#F5A623] bg-amber-50 border-y border-r border-border rounded-md p-4 mb-6" data-testid="excess-banner">
          <div className="flex items-center gap-2 mb-1">
            <SeverityBadge severity="warning">EXCESS ACKNOWLEDGED</SeverityBadge>
            <span className="text-sm font-semibold">Over-limit position diakui & terdokumentasi</span>
          </div>
          <div className="text-xs text-muted-foreground">
            Alasan: <span className="font-medium text-foreground">{f.excess_reason || "(tidak diisi)"}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="Eksposur" empty="Tidak ada eksposur tercatat" rows={data.exposures} render={(e) => (
          <div key={e.exposure_id} className="flex justify-between text-sm border-b border-border py-2">
            <div>
              <div className="mono text-xs">{e.exposure_id}</div>
              <div className="text-xs text-muted-foreground">Reporting: {formatDate(e.reporting_date)}</div>
            </div>
            <div className="text-right">
              <div className="mono">{formatIDR(e.outstanding_amount, { compact: true })}</div>
              <div className="text-xs text-muted-foreground mono">EL: {formatIDR(e.expected_loss, { compact: true })}</div>
            </div>
          </div>
        )} />

        <Section title="Collateral" empty="Belum ada collateral" rows={data.collaterals} render={(c) => (
          <div key={c.collateral_id} className="flex justify-between text-sm border-b border-border py-2">
            <div>
              <div className="font-medium">{c.collateral_type}</div>
              <div className="mono text-xs text-muted-foreground">{c.collateral_id}</div>
            </div>
            <div className="text-right">
              <div className="mono">{formatIDR(c.lendable_value, { compact: true })}</div>
              <div className="text-xs text-muted-foreground mono">Haircut: {formatPercent(c.haircut, 0)}</div>
            </div>
          </div>
        )} />

        <Section title="Guarantor" empty="Belum ada guarantor" rows={data.guarantors} render={(g) => (
          <div key={g.guarantor_id} className="flex justify-between text-sm border-b border-border py-2">
            <div>
              <div className="font-medium">{g.name}</div>
              <div className="text-xs text-muted-foreground">{g.guarantor_type} · Expires {formatDate(g.expiry_date)}</div>
            </div>
            <div className="mono">{formatIDR(g.guarantee_amount, { compact: true })}</div>
          </div>
        )} />

        <Section title="Covenant" empty="Belum ada covenant" rows={data.covenants} render={(c) => (
          <div key={c.covenant_id} className="flex justify-between text-sm border-b border-border py-2">
            <div>
              <div className="font-medium">{c.covenant_type}</div>
              <div className="text-xs text-muted-foreground mono">Threshold {c.threshold_value} · Actual {c.actual_value}</div>
            </div>
            {c.status === "Breach" ? <SeverityBadge severity="critical">BREACH</SeverityBadge> : <Badge variant="outline">OK</Badge>}
          </div>
        )} />
      </div>
    </div>
  );
}

function Section({ title, rows, render, empty }) {
  return (
    <div className="border border-border rounded-md bg-card p-4">
      <h3 className="text-sm font-bold uppercase tracking-wider mb-2">{title} ({rows.length})</h3>
      {rows.length === 0 ? <div className="text-xs text-muted-foreground py-4 text-center">{empty}</div> : rows.map(render)}
    </div>
  );
}
